import {
  isHalfWidthNumeric,
  isHalfWidthKatakana,
  FieldValidationResult,
  ValidationError,
} from './common-validation';
import { VALIDATION_MESSAGES } from './validation-messages';
import { VALIDATION_LIMITS } from './validation-limits';

import type { NoharaRepresentative } from '../../types/company.types';

/**
 * Helper function to filter visible/editable representatives
 * Returns representatives with canUpdate: true or canUpdate === undefined
 * Skips representatives with canUpdate: false (they are hidden from UI)
 */
export const getVisibleRepresentatives = (
  representatives: NoharaRepresentative[]
): NoharaRepresentative[] => {
  return representatives.filter((rep) => rep.canUpdate === true || rep.canUpdate === undefined);
};

// Shared: optional GALI code (half-width numeric, up to 9 chars)
export const validateGaliCode = (code: string, formatMessage: string): FieldValidationResult => {
  if (!code?.trim()) return { isValid: true };
  if (!isHalfWidthNumeric(code) || code.length > 9) {
    return { isValid: false, error: formatMessage };
  }
  return { isValid: true };
};

// Shared: required + max length validator
export const validateRequired = (value: string, requiredMessage: string): FieldValidationResult => {
  if (!value?.trim()) {
    return { isValid: false, error: requiredMessage };
  }
  return { isValid: true };
};

// Shared: katakana validator with optional max length
export const validateKatakana = (
  value: string,
  requiredMessage: string,
  formatMessage: string
): FieldValidationResult => {
  if (!value?.trim()) {
    return { isValid: false, error: requiredMessage };
  }
  if (!isHalfWidthKatakana(value)) {
    return { isValid: false, error: formatMessage };
  }
  return { isValid: true };
};

// Shared: representatives validator (customizable for field path and message)
export const validateRepresentativesGeneric = (
  representatives: NoharaRepresentative[],
  params: {
    salesPersonRequiredMessage: string;
    departmentRequiredMessage: string;
    maxCountMessage: string;
    getSalesPersonId: (rep: NoharaRepresentative) => string | undefined;
    salesPersonFieldPath: (index: number) => string; // e.g., (i) => `representatives.${i}.salesPerson` or `.salesPerson.id`
    maxCount?: number;
  }
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const maxCount = params.maxCount ?? 10;

  if (representatives.length > maxCount) {
    errors.push({ field: 'representatives', message: params.maxCountMessage });
  }

  representatives.forEach((rep, index) => {
    // Check if this representative has at least one field filled
    // If both department and salesPerson are empty, skip validation (it's an empty representative)
    const hasDepartment = rep.department?.trim();
    const salesPersonId = params.getSalesPersonId(rep) ?? '';
    const hasSalesPerson = salesPersonId.trim();
    const isEmptyRepresentative = !hasDepartment && !hasSalesPerson;

    // Only validate if the representative has at least one field filled
    if (!isEmptyRepresentative) {
      if (!hasDepartment) {
        errors.push({
          field: `representatives.${index}.department`,
          message: params.departmentRequiredMessage,
        });
      }

      if (!hasSalesPerson) {
        errors.push({
          field: params.salesPersonFieldPath(index),
          message: params.salesPersonRequiredMessage,
        });
      }
    }
  });

  return errors;
};

/**
 * Shared representatives validation function
 * Validates only visible/editable representatives (canUpdate: true or undefined)
 * Skips validation for records with canUpdate: false (they are hidden from UI)
 */
export const validateRepresentatives = (
  representatives: NoharaRepresentative[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Filter to only count visible representatives for max count validation
  const visibleRepresentatives = getVisibleRepresentatives(representatives);

  if (visibleRepresentatives.length > VALIDATION_LIMITS.REPRESENTATIVES.MAX_COUNT) {
    errors.push({
      field: 'representatives',
      message: VALIDATION_MESSAGES.REPRESENTATIVES_MAX_COUNT,
    });
  }

  // Only validate visible/editable representatives
  // Skip validation for records with canUpdate: false (they are hidden from UI)
  for (const [index, rep] of representatives.entries()) {
    // Skip validation for records with canUpdate: false (they are hidden from UI)
    if (rep.canUpdate === false) {
      continue;
    }

    if (!rep.department?.trim()) {
      errors.push({
        field: `representatives.${index}.department`,
        message: VALIDATION_MESSAGES.REPRESENTATIVE_DEPARTMENT_REQUIRED,
      });
    }

    if (!rep.salesPerson?.id?.trim()) {
      errors.push({
        field: `representatives.${index}.salesPerson`,
        message: VALIDATION_MESSAGES.REPRESENTATIVE_SALES_PERSON_REQUIRED,
      });
    }
  }

  return errors;
};
